import React, { useState, useEffect, useContext, useReducer } from "react";
import { useFetchApi } from "../../hooks/useFetchApi";
import TabComponent, { Tab } from "../../components/Tab";
import { StateContext } from "../../store/context";
import { TitleArea } from "../../components/Typography/titleArea";
import { StockSelector } from "./_components/stockSelector";
import { LoadingData, NoDataDetails } from "./_components/noDetails";
import ReactMarkdown from "react-markdown";

import GPT from "../../../src/static/icons/gpt.jpg";
import {
  getAIRecommendation,
  getSingleStockAIRecommendation,
  getAIStreamingResponse,
} from "../../services/ai.service";
import AdBanner from "../../components/AdBanner";
import Entitlement from "../../components/Entitlements";
import { aiSocket } from "../../config/socketService";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { usePreventCopy } from "../../hooks/usePreventCopy";
import { markdownComponents } from "../../components/markdownComponents";
import DisclaimerButton from "../../components/DisclaimerButton";
import "../../styles/theme-tokens.css";
import "../../styles/glass-tokens.css";
import "./aiscreen.theme.css";

const AiAnalyzeScreen: React.FC = (): React.ReactElement => {
  usePreventCopy();
  const {
    baseCurrency,
    authUser: { allowed, sub_plan, role_name },
  } = useContext(StateContext);

  const [primaryStock, setPrimaryStock] = useState<any>();
  const [stocksToCompare, setStocksToCompare] = useState<Array<any>>([]);
  const [stocks, setStocks] = useState<any>({});
  const [compareResults, setCompareResult] = useState<any>({});
  const [compareOtherStockResults, setOtherStockResults] = useState<any>([]);
  const [peEstimate, setPeEstimate] = useState<string>("");
  const [expectedGrowth, setExpectedGrowth] = useState<string>("");
  const [adverts, setAds] = useState<any>();
  const [t, setT] = useState<Array<Tab>>([]);
  const [peEstimatea, setPeEstimates] = useState<string | undefined>("");
  const [epsGrowth, setEpsGrowth] = useState<string | undefined>("");
  const [primaryStocks, setPrimaryStocks] = useState<any>(null);
  const [secondaryStocks, setSecondaryStocks] = useState<any>([]);
  const [scrollUpdate, setScrollUpdate] = useState<boolean>(false);
  const [aiResponse, setAiResponse] = useState("");
  const [reqLimitReached, setReqLimitReached] = useState(false);
  const [renewLimitTime, setRenewLimitTime] = useState("");
  const [loading, setLoading] = useState(false);
  const [streamingResponse, setStreamingResponse] = useState("");
  const [hasReceivedFirstChunk, setHasReceivedFirstChunk] = useState(false);
  const [metricsData, setMetricsData] = useState<any>(null);
  const [mobileScrollIndex, setMobileScrollIndex] = useState<number>(0);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const [scrollDirection, setScrollDirection] = useState<"left" | "right">(
    "left",
  );
  const {
    response,
    loading: stockLoading,
    execute: getStocks,
  } = useFetchApi("/api/all-stocks", {}, "GET");

  const {
    response: ads,
    loading: adsLoading,
    execute: getAds,
  } = useFetchApi("/api/ads", {}, "GET");

  const {
    response: metricsAndEquations,
    loading: metricsAndEquationsLoading,
    execute: getMetricsAndEquations,
  } = useFetchApi("/api/metrics-and-equations", {}, "GET");

  useEffect(() => {
    getStocks();
    getAds();
    getMetricsAndEquations();
  }, []);

  useEffect(() => {
    if (response && Object.keys(response).length) {
      setStocks(response);
    }
  }, [response]);

  useEffect(() => {
    if (ads && !adsLoading) {
      setAds(ads);
    }
  }, [ads, adsLoading]);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 640);
    };

    checkMobile();
    window.addEventListener("resize", checkMobile);

    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  useEffect(() => {
    if (metricsAndEquations && !metricsAndEquationsLoading) {
      console.log("Metrics loaded:", metricsAndEquations);
      setMetricsData(metricsAndEquations);
    }
  }, [metricsAndEquations, metricsAndEquationsLoading]);

  const handleGenerate = (
    primaryStock: any,
    stocksToCompare: any,
    peEstimate: string,
    expectedGrowth: string,
  ) => {
    setCompareResult({});
    setOtherStockResults([]);
    setT([]);
    setPrimaryStock(primaryStock);
    setStocksToCompare(stocksToCompare);

    setPeEstimate(peEstimate);
    setExpectedGrowth(expectedGrowth);
  };

  useEffect(() => {
    if (primaryStock && Object.keys(primaryStock).length) {
      setCompareResult((prev: any) => {});
      setStocksToCompare((prev: any) => []);
      setT([]);
      setTimeout(() => {
        handleGenerate(
          primaryStock,
          stocksToCompare,
          peEstimate,
          expectedGrowth,
        );
      }, 100);
    }
  }, [baseCurrency]);

  useEffect(() => {
    aiSocket.on("ai-analysis-chunk", (chunk) => {
      if (!hasReceivedFirstChunk) {
        setHasReceivedFirstChunk(true);
        setLoading(false);
      }
      setStreamingResponse((prev) => prev + chunk);
    });

    aiSocket.on("ai-analysis-complete", (finalResponse) => {
      setAiResponse(finalResponse);
      setLoading(false);
      setHasReceivedFirstChunk(false);
    });

    aiSocket.on("ai-analysis-error", (error) => {
      console.error("Analysis streaming error:", error);
      setAiResponse("An error occurred during analysis. Please try again.");
      setLoading(false);
      setHasReceivedFirstChunk(false);
    });

    return () => {
      aiSocket.off("ai-analysis-chunk");
      aiSocket.off("ai-analysis-complete");
      aiSocket.off("ai-analysis-error");
    };
  }, [hasReceivedFirstChunk]);

  const formatDateTime = (isoString: string) => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "numeric",
      hour12: true,
    });
  };

  const flattenMetrics = async (metricsDataParam: any) => {
    const flatMetrics: any[] = [];

    Object.keys(metricsDataParam).forEach((categoryKey) => {
      const categoryMetrics = metricsDataParam[categoryKey];

      if (Array.isArray(categoryMetrics)) {
        categoryMetrics.forEach((metric: any, index: number) => {
          const metricObject = {
            ...metric,
            sortableId: metric.sortableId || `${categoryKey}-${index}`,
          };
          flatMetrics.push(metricObject);
        });
      }
    });

    return flatMetrics;
  };

  const handleAnalyze = async () => {
    try {
      setLoading(true);
      setStreamingResponse("");
      setHasReceivedFirstChunk(false);

      const flattenedMetrics = await flattenMetrics(metricsData);
      console.log("flattenedMetrics: ", flattenedMetrics);

      const payLoad = {
        id: primaryStock?.id,
        primaryStock: primaryStock,
        stocks: secondaryStocks,
        stock_ids: [
          ...(primaryStock?.id ? [primaryStock.id] : []),
          ...(secondaryStocks?.map((e: any) => e.id) || []),
        ],
        selectedMetrics: flattenedMetrics.filter(
          (metric: any) =>
            metric.name !== "EPS Growth" &&
            metric.name !== "share_price_growth" &&
            metric.name !== "Dividend Payout (LTM) " &&
            metric.name !== "Dividend Yield (LTM) ",
        ),
        peEstimate: "",
        expectedGrowth: "",
        selectedStocks: [
          ...(primaryStock ? [primaryStock] : []),
          ...(secondaryStocks || []),
        ],
        filter: "quarterly",
        period: "5",
        baseCurrency: baseCurrency,
      };

      let res;
      if (secondaryStocks?.length == 0) {
        const singlePayload = {
          stock_ids: [...(primaryStock?.id ? [primaryStock.id] : [])],
          selectedStocks: [...(primaryStock?.id ? [primaryStock] : [])],
          selectedMetrics: flattenedMetrics,
          filter: "quarterly",
          period: "5",
          baseCurrency: baseCurrency,
        };

        res = await getAIRecommendation(singlePayload);
        setAiResponse(res);
      } else {
        res = await getAIRecommendation(payLoad);
        setAiResponse(res);
      }

      if (res && res.status === "AI_LIMIT_EXCEEDED" && res.nextReset) {
        setReqLimitReached(true);
        setRenewLimitTime(res.nextReset);
        setLoading(false);
      } else {
        try {
          await getAIStreamingResponse(res);
        } catch (error) {
          console.error("Error during streaming analysis:", error);
          setAiResponse("An error occurred during analysis. Please try again.");
          setLoading(false);
        }
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const isPremiumUser =
    sub_plan === "Premium Plan" && role_name === "Premium Customer"
      ? true
      : false;
  const isBasicCustomer =
    sub_plan === "Free Plan" && role_name === "Basic Customer";

  return (
    <div className="vi-ai vi-glass-sheen flex md:flex-row flex-col items-center md:items-start w-full select-none">
      <div
        className={`flex flex-col w-full ${
          isPremiumUser ? " md:w-full" : "md:!w-9/12"
        } h-full  items-start px-4 py-3 `}
      >
        <div className="w-full flex items-start justify-between gap-4">
          <TitleArea
            variant="page"
            title="AI Recommendations"
            subTitle="Data-Driven Investing, Powered by AI – Compare, Analyze, Smarter Stock Picks!"
          />
          <DisclaimerButton />
        </div>

        <hr className="hr-line w-full my-2" />

        <div className={`w-full`}>
          <StockSelector
            onHandleGenerate={handleGenerate}
            stocks={!stocks?.length ? [] : stocks}
            epsGrowth={epsGrowth || ""}
            peEstimatea={peEstimatea || ""}
            primaryStock={primaryStocks}
            secondaryStocks={secondaryStocks}
            setPrimaryStock={setPrimaryStocks}
            setSecondaryStocks={setSecondaryStocks}
            setScrollDirection={setScrollDirection}
            loading={stockLoading}
            scrollUpdate={scrollUpdate}
            setScrollUpdate={setScrollUpdate}
            mobileScrollIndex={mobileScrollIndex}
            setMobileScrollIndex={setMobileScrollIndex}
            isMobile={isMobile}
          />
          <hr className="hr-line w-full my-2" />
          <TitleArea title="Analysis" />
          {loading ? (
            <LoadingData />
          ) : !streamingResponse && !loading ? (
            <NoDataDetails />
          ) : (
            <div className="chat-container">
              <img src={GPT} className="chat-gpt-icon" alt="GPT Icon" />
              <div className="prose prose-sm !text-wrap lg:max-w-none text-[#667085] text-left font-inter overflow-x-hidden">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={markdownComponents}
                >
                  {streamingResponse}
                </ReactMarkdown>
              </div>
            </div>
          )}

          <div className="w-full flex flex-col items-end">
            {reqLimitReached && (
              <p className="text-red-600 text-sm font-bold mb-1">
                You have reached your limit for recommendations. Access will
                reset on {formatDateTime(renewLimitTime)}
              </p>
            )}
            <button
              onClick={handleAnalyze}
              className="mt-[14px] bg-[#0075E3]  !rounded-[24px] disabled:cursor-not-allowed !w-[116px] h-8 text-white font-inter"
              disabled={loading || !primaryStock || reqLimitReached}
            >
              {loading ? "Analyzing..." : "Get Analysis"}
            </button>
          </div>
        </div>
      </div>

      {/* <div
        className={`flex flex-col lg:w-3/12  ${
          false ? " hidden" : "md:!w-3/12"
        } !max-h-screen h-screen gap-8 mx-2 items-center relative px-2 py-4`}
      >
        <div className=" w-[1px] h-full bg-[#D0D5DD] absolute top-[2px]  -left-2" />
        {adverts && adverts.length ? (
          <>
            {adverts
              .filter((ad: any) => ad.section === "ai-recommendation")
              .map((ad: any) => (
                <AdBanner key={ad.id} ad={ad} />
              ))}
            <Entitlement allowedKeys={["ADD_ADS"]}>
              <AdBanner />
            </Entitlement>
          </>
        ) : null}
      </div> */}
    </div>
  );
};

export default AiAnalyzeScreen;
